import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface Topic {
  readonly topicId: string;
  readonly topicCode: string;
  readonly title: string;
  readonly topicType: string;
  readonly statusCode: string;
}

// Mirrors backend M27TopicType — server rejects any other value.
const TOPIC_TYPES = ['QA', 'ISSUE', 'PROJECT', 'APPROVAL', 'AUDIT', 'KNOWLEDGE'];

/** M27 — Discussion Board (§31). Lists discussion topics with a status filter,
 * and supports create/reply/close/reopen over discussion.controller
 * (DISCUSSION_MANAGE). */
export function M27DiscussionBoardPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<Topic[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState('');
  const [topicType, setTopicType] = useState('');

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    const q = status ? `?statusCode=${encodeURIComponent(status)}` : '';
    try {
      const r = await apiRequest<{ data: Topic[]; total: number }>(`/v1/collab/discussions${q}`, { token });
      setRows(r.data); setTotal(r.total); setError(null);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  }, [token, status]);

  useEffect(() => { void load(); }, [load]);

  async function create(): Promise<void> {
    if (!title.trim()) return;
    setBusy(true); setNotice(null);
    try {
      await apiRequest('/v1/collab/discussions', {
        token, method: 'POST',
        body: { title, topicType: topicType || undefined },
      });
      setTitle(''); setTopicType('');
      setNotice(tr('m27.discussion.noticeCreated'));
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function reply(id: string): Promise<void> {
    const text = window.prompt(tr('m27.discussion.promptReply'));
    if (text === null || !text.trim()) return;
    setBusy(true); setNotice(null);
    try {
      await apiRequest(`/v1/collab/discussions/${id}/replies`, { token, method: 'POST', body: { body: text } });
      setNotice(tr('m27.discussion.noticeReplyPosted'));
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function gate(id: string, action: 'close' | 'reopen'): Promise<void> {
    setBusy(true); setNotice(null);
    try {
      await apiRequest(`/v1/collab/discussions/${id}/${action}`, { token, method: 'POST' });
      setNotice(action === 'close' ? tr('m27.discussion.noticeClosed') : tr('m27.discussion.noticeReopened'));
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <main aria-label="discussion-board">
      <h1>{tr('m27.discussion.heading')}</h1>

      <form aria-label="topic-create-form" onSubmit={(e) => { e.preventDefault(); void create(); }}>
        <label htmlFor="topic-title">{tr('m27.discussion.fldTopicTitle')}</label>
        <input id="topic-title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        <label htmlFor="topic-type">{tr('m27.discussion.fldType')}</label>
        <select id="topic-type" value={topicType} onChange={(e) => setTopicType(e.target.value)}>
          <option value="">{tr('m27.discussion.optDefaultQa')}</option>
          {TOPIC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button type="submit" disabled={busy}>{tr('m27.discussion.btnCreate')}</button>
      </form>

      <label htmlFor="status-filter">{tr('m27.discussion.fldStatus')}</label>
      <select id="status-filter" value={status} onChange={(e) => setStatus(e.target.value)}>
        {['', 'OPEN', 'CLOSED'].map((s) => <option key={s || 'all'} value={s}>{s || tr('m27.discussion.optAll')}</option>)}
      </select>
      {notice && <p role="status">{notice}</p>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m27.discussion.loading')}</p> : (
        <>
          <p aria-label="topic-count">{tr('m27.discussion.count', { count: total })}</p>
          <table aria-label="topic-table">
            <thead><tr><th>{tr('m27.discussion.colCode')}</th><th>{tr('m27.discussion.colTitle')}</th><th>{tr('m27.discussion.colType')}</th><th>{tr('m27.discussion.colStatus')}</th><th>{tr('m27.discussion.colActions')}</th></tr></thead>
            <tbody>{rows.map((t) => (
              <tr key={t.topicId}>
                <td>{t.topicCode}</td><td>{t.title}</td><td>{t.topicType}</td><td>{t.statusCode}</td>
                <td>
                  <button type="button" aria-label={`reply-${t.topicId}`} disabled={busy} onClick={() => void reply(t.topicId)}>{tr('m27.discussion.btnReply')}</button>
                  {t.statusCode === 'CLOSED'
                    ? <button type="button" aria-label={`reopen-${t.topicId}`} disabled={busy} onClick={() => void gate(t.topicId, 'reopen')}>{tr('m27.discussion.btnReopen')}</button>
                    : <button type="button" aria-label={`close-${t.topicId}`} disabled={busy} onClick={() => void gate(t.topicId, 'close')}>{tr('m27.discussion.btnClose')}</button>}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </>
      )}
    </main>
  );
}
